import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import makeTopLevelSearch from "@saleor/hooks/makeTopLevelSearch";
import useDebounce from "@saleor/hooks/useDebounce";
import gql from "graphql-tag";
import { useState } from "react";

import { QueryProducts, QueryProductsVariables } from "./types/QueryProducts";

export const queryProducts = gql`
  ${pageInfoFragment}
  query QueryProducts(
    $after: String
    $first: Int!
    $searchByName: String
    $searchBySku: String
    $query: String!
    $brands: [ID]
    $productStatus: ProductStatusEnum
    $isPublished: Boolean
    $categories: [ID]
    $malls: [ID]
    $userViewInfo: ProductUserViewInfoInput
    $isVisible: ProductVisibilityFilterInput
  ) {
    search: products(
      after: $after
      first: $first
      filter: {
        searchByName: $searchByName
        searchBySku: $searchBySku
        search: $query
        brands: $brands
        productStatus: $productStatus
        isPublished: $isPublished
        categories: $categories
        malls: $malls
        isVisible: $isVisible
      }
      userViewInfo: $userViewInfo
    ) {
      edges {
        node {
          id
          name
          thumbnail {
            url
          }
          variants {
            id
            name
            price {
              currency
              amount
            }
            sku
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
      totalCount
    }
  }
`;

const useProductSearchQuery = makeTopLevelSearch<
  QueryProducts,
  QueryProductsVariables
>(queryProducts);

function useProductQuery(variables: QueryProductsVariables) {
  const [productKeyword, setProductKeyword] = useState("");
  const setProductKeywordDebounced = useDebounce(setProductKeyword);
  const [searchBy, setSearchBy] = useState("sku");

  const { loadMore, result } = useProductSearchQuery({
    skip:
      (variables.brands?.length ?? 0) === 0 &&
      (variables.malls?.length ?? 0) === 0,
    variables: {
      ...variables,
      searchByName: searchBy === "name" ? productKeyword : "",
      searchBySku: searchBy === "sku" ? productKeyword : ""
    }
  });

  return {
    handleProductSearch: setProductKeywordDebounced,
    handleSearchBy: setSearchBy,
    loadMore,
    productKeyword,
    result
  };
}

export default useProductQuery;
